import React, { useState } from 'react';
import { 
  Box, 
  TextField, 
  Button, 
  Stack, 
  Grid2 as Grid, 
  Typography, 
  Paper, 
  alpha, 
  CircularProgress 
} from '@mui/material';
import { 
  SaveOutlined, 
  CloseOutlined, 
  InfoOutlined 
} from '@mui/icons-material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { message } from 'antd';
import { updateParameter } from '../../../../helpers/api';

const EditParamterForm = ({ data, closeModal, reload }) => {
  const [loading, setLoading] = useState(false);

  const renderSymbol = () => {
    if (data?.nesreaDataType === 'LESS_THAN') return '<';
    if (data?.nesreaDataType === 'GREATER_THAN') return '>';
    return '';
  };

  const formik = useFormik({
    initialValues: {
      unit: data?.unit || '',
      nesrea: `${renderSymbol()} ${data?.nesrea || ''}`.trim(),
      world: `${renderSymbol()} ${data?.world || ''}`.trim(),
    },
    validationSchema: Yup.object({
      nesrea: Yup.string().required('NESREA Limit is required'),
    }),
    onSubmit: (values) => {
      setLoading(true);
      const payload = {
        parameterId: data?.parameterId,
        measurementId: data?.measurementId,
        nesreaUpperLimit: 0,
        nesreaLowerLimit: 0,
        worldUpperLimit: 0,
        worldLowerLimit: 0,
        nesrea: values.nesrea,
        world: values.world,
      };

      updateParameter(payload)
        .then(() => {
          message.success(`Updated successfully!`);
          setLoading(false);
          reload();
          closeModal();
        })
        .catch((error) => {
          message.error(`Something went wrong. Please try again`);
          setLoading(false);
        });
    },
  });

  const textFieldStyle = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
    },
  };

  return (
    <Box component="form" onSubmit={formik.handleSubmit}>
      <Grid container spacing={3}>
        <Grid size={12}>
          <TextField
            fullWidth
            label="Parameter Name"
            name="unit"
            value={formik.values.unit}
            disabled
            size="small"
            sx={textFieldStyle}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            label="NESREA Limit"
            name="nesrea"
            size="small"
            value={formik.values.nesrea}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.nesrea && Boolean(formik.errors.nesrea)}
            helperText={formik.touched.nesrea && formik.errors.nesrea}
            sx={textFieldStyle}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            label="World Bank Limit"
            name="world"
            size="small"
            value={formik.values.world}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            sx={textFieldStyle}
          />
        </Grid>

        {/* HINT SECTION */}
        <Grid size={12}>
          <Paper
            elevation={0}
            sx={{
              p: 1.5,
              bgcolor: alpha('#0288d1', 0.05),
              borderRadius: '10px',
              border: '1px dashed',
              borderColor: alpha('#0288d1', 0.3),
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
              <InfoOutlined sx={{ fontSize: 16, color: 'info.main' }} />
              <Typography variant="caption" fontWeight={700} color="info.main">
                FORMATTING GUIDE
              </Typography>
            </Stack>
            <Typography variant="caption" color="text.secondary" display="block">
              Use <strong>{`<`}</strong> (Less Than), <strong>{`>`}</strong> (Greater Than), or <strong>{`-`}</strong> (Range).
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
               <Box component="span" sx={{ bgcolor: alpha('#0288d1', 0.1), px: 1, py: 0.2, borderRadius: '4px', fontSize: '0.7rem' }}>{'< 8.7'}</Box>
               <Box component="span" sx={{ bgcolor: alpha('#0288d1', 0.1), px: 1, py: 0.2, borderRadius: '4px', fontSize: '0.7rem' }}>{'> 8.7'}</Box>
               <Box component="span" sx={{ bgcolor: alpha('#0288d1', 0.1), px: 1, py: 0.2, borderRadius: '4px', fontSize: '0.7rem' }}>{'8.7 - 9.7'}</Box>
            </Stack>
          </Paper>
        </Grid>

        <Grid size={12}>
          <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 1 }}>
            <Button
              variant="text"
              color="inherit"
              onClick={closeModal}
              disabled={loading}
              sx={{ textTransform: 'none', fontWeight: 700 }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disableElevation
              disabled={loading}
              startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <SaveOutlined />}
              sx={{ 
                textTransform: 'none', 
                fontWeight: 700, 
                borderRadius: '8px',
                px: 4,
                bgcolor: 'success.main',
                '&:hover': { bgcolor: 'success.dark' }
              }}
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </Button>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
};

export default EditParamterForm;